import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SignatureCaptureDialog from '../components/installer/SignatureCaptureDialog';
import SignatureImage from '../components/shared/SignatureImage';

const InstallerDashboard = ({ user, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 639px)').matches);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const fetchOrders = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await axios.get(`${API}/orders`, { withCredentials: true });
      setOrders(res.data || []);
    } catch (err) {
      toast.error('Failed to load assigned orders');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  const updateInstallationStatus = async (orderId, status, extraPayload = {}) => {
    try {
      const res = await axios.put(`${API}/orders/${orderId}`, { status, ...extraPayload }, { withCredentials: true });
      const updatedOrder = res?.data;

      const mergeOrder = (prevOrder) => {
        if (!prevOrder) return prevOrder;
        const delta = updatedOrder && Object.keys(updatedOrder).length > 0 ? updatedOrder : { status };
        return { ...prevOrder, ...delta };
      };

      // Keep local state in sync so the dialog updates immediately
      setOrders((prev) => prev.map((o) => (o.id === orderId ? mergeOrder(o) : o)));
      setSelectedOrder((prev) => (prev && prev.id === orderId ? mergeOrder(prev) : prev));

      // Close dialog after successful action
      setSelectedOrder(null);

      toast.success(status === 'approved' ? 'Order reopened' : `Marked as ${status}`);
      // Refresh orders in the background without blanking the dialog view
      fetchOrders(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const openSignatureFlow = () => {
    if (!selectedOrder?.id) return;
    setSignatureDialogOpen(true);
  };

  const handleSignatureSave = async ({ signatureBlob, customerName }) => {
    if (!selectedOrder?.id) return;

    setSavingSignature(true);
    try {
      const formData = new FormData();
      formData.append('orderId', selectedOrder.id);
      formData.append('signature', signatureBlob, `${selectedOrder.id}-signature.png`);
      if (customerName) {
        formData.append('customerName', customerName);
      }

      const uploadResponse = await axios.post(`${API}/upload-signature`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const signatureUrl = uploadResponse?.data?.url;
      if (!signatureUrl) {
        throw new Error('Missing signature URL from upload response');
      }

      await updateInstallationStatus(selectedOrder.id, 'completed', {
        signatureUrl,
        signatureKey: uploadResponse?.data?.key || null,
        signatureCustomerName: customerName || null
      });

      setSignatureDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to save signature');
    } finally {
      setSavingSignature(false);
    }
  };

  const handlePrimaryAction = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseDialog = () => {
    setSelectedOrder(null);
  };

  const torontoTimeZone = 'America/Toronto';

  // Return YYYY-MM-DD key. If input is a date-only string (YYYY-MM-DD), use it directly.
  // Otherwise, format the date in Toronto timezone to avoid off-by-one due to UTC parsing.
  const dateKeyInToronto = (date) => {
    if (!date) return null;
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date; // already a date-only key; don't shift timezones
    }
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('en-CA', { timeZone: torontoTimeZone }).format(d);
  };

  const todayKey = dateKeyInToronto(new Date());

  const bucketKey = (order) => {
    const installKey = dateKeyInToronto(order.installationDate);
    if (!installKey || !todayKey) return null;
    if (installKey === todayKey) return 'today';
    if (installKey > todayKey) return 'upcoming';
    return 'past';
  };

  const badgeStyle = {
    base: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '4px 10px',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    variants: {
      today: { background: '#E0F2FE', color: '#0B72B9' },
      upcoming: { background: '#EDE9FE', color: '#6B21A8' },
      in_process: { background: '#FEF3C7', color: '#92400E' },
      approved: { background: '#DCFCE7', color: '#166534' },
      completed: { background: '#E5E7EB', color: '#374151' },
      default: { background: '#F3F4F6', color: '#111827' }
    }
  };

  const statusBadge = (order) => {
    const keyRaw = order?.status || 'default';
    const key = keyRaw === 'in_process' ? 'in_process' : keyRaw;
    const variant = badgeStyle.variants[key] || badgeStyle.variants.default;
    return (
      <span style={{ ...badgeStyle.base, ...variant }}>
        {key.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const primaryButtonLabel = (status) => {
    if (status === 'completed') return 'View';
    if (status === 'in_process') return 'Continue';
    if (status === 'approved') return 'Open Order';
    return 'Open Order';
  };

  const tabConfig = useMemo(() => {
    const todayCount = orders.filter(o => bucketKey(o) === 'today').length;
    const upcomingCount = orders.filter(o => bucketKey(o) === 'upcoming' && o.status !== 'completed').length;
    const pendingCount = orders.filter(o => o.status === 'approved').length;
    const completedCount = orders.filter(o => o.status === 'completed').length;
    return [
      { key: 'today', label: 'Today', count: todayCount },
      { key: 'upcoming', label: 'Upcoming', count: upcomingCount },
      { key: 'in_process', label: 'Pending', count: pendingCount },
      { key: 'completed', label: 'Completed', count: completedCount },
      { key: 'all', label: 'All Jobs' }
    ];
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const b = bucketKey(o);
      if (activeTab === 'today') return b === 'today';
      if (activeTab === 'upcoming') return b === 'upcoming' && o.status !== 'completed';
      if (activeTab === 'in_process') return o.status === 'approved';
      if (activeTab === 'completed') return o.status === 'completed';
      return true;
    });
  }, [orders, activeTab]);

  const kpiCards = [
    { key: 'today', label: 'Today Jobs', value: tabConfig.find(t => t.key === 'today')?.count || 0, color: '#0B72B9', bg: '#E0F2FE' },
    { key: 'upcoming', label: 'Upcoming Jobs', value: tabConfig.find(t => t.key === 'upcoming')?.count || 0, color: '#6B21A8', bg: '#EDE9FE' },
    { key: 'completed', label: 'Completed Jobs', value: tabConfig.find(t => t.key === 'completed')?.count || 0, color: '#374151', bg: '#E5E7EB' }
  ];

  const renderTable = (list) => (
    <div className="table-container" style={{ marginBottom: '24px', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
        <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          <tr>
            <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Order ID</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Customer</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Install Date</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Store Invoice #</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Amount</th>
            <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700 }}>Status</th>
            <th style={{ textAlign: 'right', padding: '10px 12px', fontWeight: 700 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map(order => (
            <tr
              key={order.id}
              style={{ cursor: 'pointer' }}
              onClick={() => handlePrimaryAction(order)}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePrimaryAction(order); }}
              tabIndex={0}
            >
              <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '13px', borderBottom: '1px solid #E5E7EB' }}>{order.id ? order.id.substring(0,8) : '—'}</td>
              <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB' }}>{order.customer?.name}</td>
              <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB' }}>{order.installationDate || '-'}</td>
              <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB' }}>{order.storeInvoiceNumber || '-'}</td>
              <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB' }}>${order.totals?.total || order.totals?.subtotal || '0'}</td>
              <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB' }}>{statusBadge(order)}</td>
              <td style={{ padding: '12px', borderBottom: '1px solid #E5E7EB', textAlign: 'right' }}>
                <button
                  className="btn btn-primary"
                  style={{ background: '#FDB515', color: '#000', minHeight: '44px' }}
                  onClick={(e) => { e.stopPropagation(); handlePrimaryAction(order); }}
                >
                  {primaryButtonLabel(order.status)}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {list.length === 0 && <div style={{ textAlign:'center', padding:'32px', color:'#888' }}>No jobs scheduled<br/>Check Upcoming or All Jobs</div>}
    </div>
  );

  const renderCards = (list) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {list.map(order => (
        <div
          key={order.id}
          onClick={() => handlePrimaryAction(order)}
          onKeyDown={(e) => { if (e.key === 'Enter') handlePrimaryAction(order); }}
          tabIndex={0}
          style={{
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            padding: '12px',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: '8px' }}>
            <div style={{ fontWeight: 700 }}>{order.customer?.name || '—'}</div>
            {statusBadge(order)}
          </div>
          <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
            {(order.id ? order.id.substring(0,8) : '—')} • {order.installationDate || '-'}
          </div>
          <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>
            Invoice: {order.storeInvoiceNumber || '—'}
          </div>
          <div style={{ paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <button
              className="btn btn-primary"
              style={{
                background: '#FDB515',
                color: '#000',
                width: '100%',
                height: '50px',
                borderRadius: '10px',
                fontWeight: 600,
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={(e) => { e.stopPropagation(); handlePrimaryAction(order); }}
            >
              {primaryButtonLabel(order.status)}
            </button>
          </div>
        </div>
      ))}
      {list.length === 0 && <div style={{ textAlign:'center', padding:'32px', color:'#888' }}>No jobs scheduled<br/>Check Upcoming or All Jobs</div>}
    </div>
  );

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div style={{ padding:'24px', background: '#F9FAFB', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px', gap: '12px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <img
            src="/Install-Now.svg"
            alt="Install Now logo"
            style={{ width:'40px', height:'40px', borderRadius:'12px', objectFit:'cover', background:'#FFFFFF' }}
          />
          <div>
            <h1 style={{ fontSize:'22px', fontWeight:800, margin:0 }}>Installer Dashboard</h1>
            <div style={{ color:'#555', marginTop:'4px', fontSize:'13px' }}>Assigned to: {user.name}</div>
          </div>
        </div>
        <button className="btn btn-secondary" onClick={onLogout}>Logout</button>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'10px', marginBottom:'12px', overflowX:'auto', paddingBottom:'6px' }}>
        {tabConfig.map(tab => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                borderRadius: '999px',
                border: '1px solid #E5E7EB',
                background: isActive ? '#111827' : '#F3F4F6',
                color: isActive ? '#FFFFFF' : '#111827',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                boxShadow: isActive ? '0 2px 6px rgba(0,0,0,0.16)' : 'none',
                transition: 'all 120ms ease'
              }}
            >
              <span>{tab.label}</span>
              {typeof tab.count === 'number' && (
                <span style={{ background: isActive ? '#FBBF24' : '#111827', color: isActive ? '#111827' : '#FFFFFF', borderRadius:'999px', padding:'3px 9px', fontSize:'12px', fontWeight:800 }}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:'12px', marginBottom:'16px' }}>
        {kpiCards.map(card => (
          <div
            key={card.key}
            onClick={() => setActiveTab(card.key)}
            style={{
              cursor:'pointer',
              borderRadius:'12px',
              padding:'12px',
              background: card.bg,
              border:'1px solid #E5E7EB'
            }}
          >
            <div style={{ fontSize:'13px', color:'#374151', marginBottom:'6px' }}>{card.label}</div>
            <div style={{ fontSize:'22px', fontWeight:800, color: card.color }}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Content */}
      {isMobile ? renderCards(filteredOrders) : renderTable(filteredOrders)}

      <Dialog open={!!selectedOrder} onOpenChange={handleCloseDialog}>
        <DialogContent style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogHeader>
            <DialogTitle>
              Order Details - {selectedOrder?.id ? selectedOrder.id.substring(0, 8) : '—'}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div>
              <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {statusBadge(selectedOrder)}
                <button
                  className="btn btn-primary"
                  style={{ background: '#FDB515', color: '#000', minHeight: '44px' }}
                  onClick={() => {
                    if (selectedOrder.status === 'completed') {
                      updateInstallationStatus(selectedOrder.id, 'approved');
                      return;
                    }
                    openSignatureFlow();
                  }}
                >
                  {selectedOrder.status === 'completed' ? 'Reopen' : 'Mark Completed'}
                </button>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Customer Information</h3>
                <div style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px' }}>
                  <p><strong>Name:</strong> {selectedOrder.customer?.name}</p>
                  <p><strong>Email:</strong> {selectedOrder.customer?.email}</p>
                  <p><strong>Phone:</strong> {selectedOrder.customer?.phone}</p>
                  <p><strong>Address:</strong> {selectedOrder.customer?.address}</p>
                  <p><strong>Installation Date:</strong> {selectedOrder.installationDate || '-'}</p>
                  {selectedOrder.storeInvoiceNumber && (
                    <p><strong>Store Invoice #:</strong> {selectedOrder.storeInvoiceNumber}</p>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '12px' }}>Items</h3>
                {(selectedOrder.items || []).map((item, idx) => (
                  <div key={idx} style={{ background: '#FAFAFA', padding: '16px', borderRadius: '12px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '600' }}>{item.name}</span>
                      <span style={{ fontWeight: '700' }}>${item.price} x {item.qty}</span>
                    </div>
                    {item.notes && (
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px', padding: '8px', background: 'white', borderRadius: '6px' }}>
                        <strong>Notes:</strong> {item.notes}
                      </div>
                    )}
                    <div style={{ fontSize: '13px', color: '#666' }}>Spiff: ${item.spiff}</div>
                  </div>
                ))}
              </div>

              {selectedOrder.notes && (
                <div style={{ marginBottom: '20px', padding: '16px', background: '#FFF9E6', borderRadius: '12px', border: '1px solid #FDB515' }}>
                  <strong style={{ fontSize: '14px' }}>Order Notes:</strong>
                  <div style={{ marginTop: '8px', color: '#333', whiteSpace: 'pre-wrap' }}>{selectedOrder.notes}</div>
                </div>
              )}

              {selectedOrder.signature_url && (
                <div style={{ marginBottom: '20px', padding: '16px', background: '#FAFAFA', borderRadius: '12px' }}>
                  <strong style={{ fontSize: '14px' }}>Customer Signature:</strong>
                  {selectedOrder.signature_customer_name && (
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#374151' }}>
                      Signed by: {selectedOrder.signature_customer_name}
                    </div>
                  )}
                  {selectedOrder.completed_at && (
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#6B7280' }}>
                      Completed at: {new Date(selectedOrder.completed_at).toLocaleString()}
                    </div>
                  )}
                  <SignatureImage
                    src={`${API}/orders/${selectedOrder.id}/signature-image`}
                    alt="Customer signature"
                    style={{ marginTop: '10px', width: '100%', maxWidth: '420px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#fff' }}
                  />
                </div>
              )}

              <div style={{ background: '#FDB515', padding: '20px', borderRadius: '12px', color: '#000' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600' }}>Subtotal:</span>
                  <span style={{ fontWeight: '700' }}>${selectedOrder.totals?.subtotal || selectedOrder.totals?.total || '0'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600' }}>Total Spiff:</span>
                  <span style={{ fontWeight: '700' }}>${selectedOrder.totals?.spiffTotal || '0'}</span>
                </div>
                <div style={{ borderTop: '2px solid rgba(0,0,0,0.2)', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '18px', fontWeight: '800' }}>Total:</span>
                  <span style={{ fontSize: '18px', fontWeight: '800' }}>${selectedOrder.totals?.total || selectedOrder.totals?.subtotal || '0'}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SignatureCaptureDialog
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        onSave={handleSignatureSave}
        saving={savingSignature}
        defaultCustomerName={selectedOrder?.customer?.name || ''}
      />
    </div>
  );
};

export default InstallerDashboard;
